// Single-click-to-copy <pre> block. Click anywhere on the block to
// copy its text; a brief flash confirms success. Silent on failure
// (no clipboard permission, insecure context, etc.).
//
// Used in Studio surfaces where operators repeatedly grab rendered
// LLM input/output for pasting into elsewhere — Test Run (OUTPUT,
// RENDERED INPUT), Observability call detail (INPUT, OUTPUT).
//
// Why a wrapper rather than reaching for native double-click selection:
// the targets are multi-line and can be thousands of characters; a
// single click is a noticeable improvement over hunting for the right
// drag origin.

import { useState } from 'react'

type Props = {
  text: string
  /** Extra Tailwind classes for the <pre>. Caller controls layout
   *  (max-height, font size, padding, etc.) so this component stays
   *  styling-agnostic. */
  className?: string
  /** Tooltip override. Default: "Click to copy". */
  title?: string
}

export function CopyablePre({ text, className = '', title = 'Click to copy' }: Props) {
  const [flash, setFlash] = useState(false)
  const onClick = () => {
    if (!text) return
    try {
      navigator.clipboard.writeText(text)
      setFlash(true)
      window.setTimeout(() => setFlash(false), 1200)
    } catch { /* silent — insecure context / permissions denied */ }
  }
  return (
    <pre
      onClick={onClick}
      title={title}
      className={`${className} ${flash ? 'me-flash-copy' : ''}`.trim()}
    >
      {text}
    </pre>
  )
}
